import { create } from 'zustand';

/**
 * Per-file upload tracker for the pack editor's drag-drop uploader.
 *
 * Each entry represents one file the admin tried to upload. The state
 * machine is:
 *
 *     pending → hashing → uploading → done
 *                                ↘ error → (retry) → hashing → ...
 *
 * Items in `done` stay in the list until the editor clears them (we
 * keep them around for a few seconds so the admin can see the success
 * indicator). Items in `error` stay until the admin clicks "Thử lại"
 * (which moves them back to `pending`) or removes them.
 *
 * The store is keyed by a `tempUploadId = crypto.randomUUID()` chosen
 * by the editor when the file is dropped. The same id becomes the
 * `assetId` on success — the Worker accepts a client-chosen id under
 * `PUT /admin/v1/packs/:packId/assets/:assetId` (the route is
 * idempotent per Requirement 29.1, so collisions on retry are safe).
 *
 * Validates Requirements 22.2, 25.1.
 */

export type UploadStatus =
  | 'pending'
  | 'hashing'
  | 'uploading'
  | 'done'
  | 'error';

export interface UploadEntry {
  /** Stable id chosen at drop time; reused as the Worker's `assetId`. */
  tempUploadId: string;
  fileName: string;
  byteSize: number;
  contentType: string;
  status: UploadStatus;
  /** 0..1 progress for the `uploading` state; meaningless otherwise. */
  progress: number;
  /** Hex SHA-256 once the hashing step finishes. */
  sha256: string | null;
  /** Vietnamese error reason on `error`; null otherwise. */
  error: string | null;
}

interface UploadStore {
  uploads: UploadEntry[];
  add(entry: Omit<UploadEntry, 'progress' | 'sha256' | 'error' | 'status'> & {
    status?: UploadStatus;
  }): void;
  setStatus(tempUploadId: string, status: UploadStatus): void;
  setProgress(tempUploadId: string, progress: number): void;
  setSha256(tempUploadId: string, sha256: string): void;
  setError(tempUploadId: string, error: string): void;
  remove(tempUploadId: string): void;
  /** Drop every `done` entry — used after a refetch to clear the success row. */
  clearDone(): void;
  /** Drop every entry — used when navigating away from the editor. */
  reset(): void;
}

export const useUploadStore = create<UploadStore>((set) => ({
  uploads: [],
  add: (entry) =>
    set((state) => ({
      uploads: [
        ...state.uploads,
        {
          tempUploadId: entry.tempUploadId,
          fileName: entry.fileName,
          byteSize: entry.byteSize,
          contentType: entry.contentType,
          status: entry.status ?? 'pending',
          progress: 0,
          sha256: null,
          error: null,
        },
      ],
    })),
  setStatus: (tempUploadId, status) =>
    set((state) => ({
      uploads: state.uploads.map((u) =>
        u.tempUploadId === tempUploadId
          ? { ...u, status, error: status === 'error' ? u.error : null }
          : u,
      ),
    })),
  setProgress: (tempUploadId, progress) =>
    set((state) => ({
      uploads: state.uploads.map((u) =>
        u.tempUploadId === tempUploadId ? { ...u, progress } : u,
      ),
    })),
  setSha256: (tempUploadId, sha256) =>
    set((state) => ({
      uploads: state.uploads.map((u) =>
        u.tempUploadId === tempUploadId ? { ...u, sha256 } : u,
      ),
    })),
  setError: (tempUploadId, error) =>
    set((state) => ({
      uploads: state.uploads.map((u) =>
        u.tempUploadId === tempUploadId ? { ...u, status: 'error', error } : u,
      ),
    })),
  remove: (tempUploadId) =>
    set((state) => ({
      uploads: state.uploads.filter((u) => u.tempUploadId !== tempUploadId),
    })),
  clearDone: () =>
    set((state) => ({
      uploads: state.uploads.filter((u) => u.status !== 'done'),
    })),
  reset: () => set({ uploads: [] }),
}));
